import Chip from "@mui/material/Chip";
import { useNavigate } from "react-router-dom";
import CrudListPage, { type Column, type FormField, type RowAction } from "../../shared/components/CrudListPage";
import { type UserRead, usersApi } from "../../core/api/usersApi";

const PROVIDER_LABEL: Record<string, string> = {
  local: "Local",
  google: "Google",
  microsoft: "Microsoft",
};

const columns: Column<UserRead>[] = [
  { key: "name", label: "Nombre" },
  { key: "email", label: "Email" },
  {
    key: "provider",
    label: "Proveedor",
    render: (u) => PROVIDER_LABEL[u.provider] ?? u.provider,
  },
  {
    key: "active",
    label: "Estado",
    render: (u) => (
      <Chip
        label={u.active ? "Activo" : "Inactivo"}
        size="small"
        sx={{
          bgcolor: u.active ? "rgba(22,163,74,0.12)" : "rgba(220,38,38,0.12)",
          color: u.active ? "var(--color-success)" : "var(--color-danger)",
          fontWeight: 600,
          border: "none",
        }}
      />
    ),
  },
];

const createFields: FormField[] = [
  {
    key: "name",
    label: "Nombre",
    type: "text",
    validate: (v) => (v.trim().length < 2 ? "Mínimo 2 caracteres." : undefined),
  },
  {
    key: "email",
    label: "Email",
    type: "email",
    validate: (v) => {
      const parts = v.split("@");
      return parts.length !== 2 || !parts[0] || !parts[1].includes(".") ? "Email no válido." : undefined;
    },
  },
  {
    key: "provider",
    label: "Proveedor",
    type: "select",
    options: [
      { value: "local", label: "Local" },
      { value: "google", label: "Google" },
      { value: "microsoft", label: "Microsoft" },
    ],
  },
  {
    key: "password",
    label: "Contraseña",
    type: "password",
    hidden: (v) => v.provider !== "local",
    validate: (v, all) =>
      all.provider === "local" && !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/.test(v)
        ? "Mín. 8 caracteres, 1 mayúscula, 1 minúscula, 1 dígito y 1 especial."
        : undefined,
  },
];

const fetchPage = async (page: number, size: number) => {
  const result = await usersApi.list(page, size);
  return { items: result.items, total: result.total };
};

const onSubmitCreate = async (data: Record<string, string>) => {
  await usersApi.create({
    name: data.name,
    email: data.email,
    provider: data.provider as "local" | "google" | "microsoft",
    password: data.provider === "local" ? data.password : undefined,
  });
};

const UserList = () => {
  const navigate = useNavigate();

  const actions: RowAction<UserRead>[] = [
    {
      label: "Ver perfil",
      onClick: (u: UserRead) => navigate(`/profile/${u.id}`),
    },
  ];

  return (
    <CrudListPage
      title="Gestión de usuarios"
      columns={columns}
      fetchPage={fetchPage}
      searchKeys={["name", "email"]}
      actions={actions}
      rowSx={(u) => ({ opacity: u.active ? 1 : 0.45 })}
      createLabel="Nuevo usuario"
      createFields={createFields}
      onSubmitCreate={onSubmitCreate}
    />
  );
};

export default UserList;
